import { objectKeys } from '@advanza/func'
import { SelectCheckbox } from '@advanza/input'
import { Divider, Icon } from '@advanza/ui'
import { capitalize } from '@trustoo/funcs/string'
import { Log } from '@trustoo/types/entity/log'
import { SystemLog, SystemLogRelation } from '@trustoo/types/entity/systemLog'
import { fetchAdminUsers } from 'actions/adminUsers'
import Card from 'components/Card'
import AutoRow from 'components/misc/AutoRow'
import TableList from 'components/TableList'
import LoadBoxWrapper from 'components/ui/LoadBoxWrapper'
import { formatRelative } from 'date'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { SelectableLogType, useLogsList, useLogTypes } from 'hooks/logsHook'
import User from 'interfaces/User'
import { Fragment, ReactNode, useEffect } from 'react'

export type LogUnion = Log | SystemLog

export interface Changes {
    [field: string]: {
        old?: unknown
        new?: unknown
    }
}

export const NOT_RENDERED = 'NOT_RENDERED'

const DEFINITIONS = {
    foreign_key: { name: '#' },
    message: { name: 'message' },
    log_type: { name: 'type' },
    created: { name: 'created' },
    user_id: { name: 'user' },
    userRole: { name: 'role' },
}

export const formatMessageByLogType = (log: LogUnion) => {
    const { log_type, message, message_json } = log

    switch (log_type) {
        case 'geo':
        case 'filters':
        case 'leadSettingsLimits':
        case 'monthPickerAvailability':
        case 'paymentData':
        case 'paymentDataInternal':
        case 'contactData':
        case 'profile':
        case 'expectation_settings':
            return (
                <Fragment key="message">
                    {formatJsonChanges(message_json)}
                    {log_type === 'expectation_settings' && message && (
                        <div className="italic whitespace-pre-wrap">Note: {message}</div>
                    )}
                </Fragment>
            )
        default:
            return message || '-'
    }
}

const formatJsonChanges = (jsonData: unknown) => {
    if (!jsonData) {
        return '-'
    }

    try {
        const formattedLines: { key: string; value: unknown }[] = []
        const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData

        Object.keys(parsed).forEach((key) => {
            const formattedKey = capitalize(key).replace(/_/g, ' ')
            const value = parsed[key]

            if (
                value &&
                typeof value === 'object' &&
                Object.hasOwn(value, 'new') &&
                Object.hasOwn(value, 'old')
            ) {
                // Preserve original formatting by using the values as they are
                const oldValue = value.old ?? '-'
                const newValue = value.new ?? '-'
                formattedLines.push({
                    key: formattedKey,
                    value: `${oldValue} → ${newValue}`,
                })
            } else {
                // Handle simple key-value pairs
                const displayValue = value ?? '-'
                formattedLines.push({
                    key: formattedKey,
                    value: displayValue,
                })
            }
        })

        return (
            <div className="text-[13px] leading-[1.3]">
                {formattedLines.map((line, index) => (
                    <div key={index} className="mb-1">
                        <div className="mb-0.5 ">
                            <span className="font-bold underline mr-1">{line.key}:</span>
                            <span className="w-[600px] break-all">{String(line.value)}</span>
                        </div>
                    </div>
                ))}
            </div>
        )
    } catch (e) {
        return String(jsonData) || '-'
    }
}

interface Options {
    pageSize?: number
    logTypes?: string[]
    excludeLogTypes?: string[]
    providerIds?: number[]
    query?: string
    relation?: SystemLogRelation
    foreignKey?: number
}

interface Props {
    isProLogs?: boolean
    filterId?: string
    options?: Options
    selectableLogTypes?: SelectableLogType[]
    allowEmptySelection?: boolean
    hideLogTypeSelection?: boolean
    showCols?: (keyof typeof DEFINITIONS)[]
    renderCol?: (log: LogUnion, col: string) => ReactNode
}

const LogsList = ({
    isProLogs = true,
    filterId = 'all',
    options = {},
    selectableLogTypes,
    allowEmptySelection = true,
    hideLogTypeSelection = false,
    showCols,
    renderCol,
}: Props) => {
    const dispatch = useAppDispatch()
    const {
        filter = {},
        currentPage,
        changeFilter,
        entities,
        ...rest
    } = useLogsList(isProLogs, filterId, options)
    const logTypes = useLogTypes(isProLogs, { selectableLogTypes, relation: options.relation })
    const proUsers: Record<number, User> | undefined = useAppSelector(
        (state) => state.providers.entities.users,
    )
    const adminUsers: Record<number, User> | undefined = useAppSelector(
        (state) => state.adminUsers.entities.adminUsers,
    )

    useEffect(() => {
        dispatch(fetchAdminUsers())
    }, [dispatch])

    const definitions: Partial<typeof DEFINITIONS> = Object.fromEntries(
        Object.entries(DEFINITIONS).filter(([key]) =>
            showCols ? showCols.includes(key as keyof typeof DEFINITIONS) : key !== 'foreign_key',
        ),
    )

    const renderRow = (id: number) => {
        const log = (isProLogs ? entities.logs : entities.system_logs)[id]
        const error =
            typeof log.message_json === 'object' &&
            log.message_json &&
            'error' in log.message_json &&
            log.message_json.error

        return {
            id,
            cols: renderCols(log),
            expandOnClick: Boolean(error),
            expandable: error ? <Card expandableStyle>{error}</Card> : undefined,
        }
    }

    const renderCols = (log: LogUnion) => {
        const proUserWhoLogged = proUsers?.[log.user_id ?? 0]
        const adminUserWhoLogged = adminUsers?.[log.user_id ?? 0]
        const userRole =
            proUserWhoLogged &&
            'service_provider_id' in log &&
            proUserWhoLogged.service_provider_id === log.service_provider_id
                ? 'pro'
                : adminUserWhoLogged
                  ? 'office'
                  : ''

        return objectKeys(definitions).map((key) => {
            if (renderCol) {
                const rendered = renderCol(log, key)

                if (rendered !== NOT_RENDERED) {
                    return rendered
                }
            }

            switch (key) {
                case 'foreign_key':
                    return 'foreign_key' in log ? log.foreign_key : undefined
                case 'message':
                    return formatMessageByLogType(log)
                case 'created':
                    return formatRelative(log.created, { includeTime: true })
                case 'user_id':
                    return userRole === 'pro'
                        ? proUserWhoLogged?.first_name + ' ' + proUserWhoLogged?.last_name
                        : userRole === 'office'
                          ? adminUserWhoLogged?.first_name + ' ' + adminUserWhoLogged?.last_name
                          : ''
                case 'userRole':
                    return userRole
                default:
                    return log[key]
            }
        })
    }

    return (
        <LoadBoxWrapper isLoading={rest.isFetching}>
            <Divider m />
            <AutoRow>
                {!hideLogTypeSelection && (
                    <SelectCheckbox
                        large
                        placeholder="Log types"
                        options={
                            selectableLogTypes ||
                            logTypes
                                .filter(
                                    (logType) => !(options.excludeLogTypes || []).includes(logType),
                                )
                                .map((logType) => ({
                                    name: logType || '<empty>',
                                    value: logType || 'empty',
                                }))
                        }
                        value={filter.logTypes || []}
                        onChangeValue={(logTypes) =>
                            (allowEmptySelection || logTypes.length) && changeFilter({ logTypes })
                        }
                    />
                )}
                <button>
                    <Icon
                        name={rest.isFetching ? 'hourglass_full' : 'refresh'}
                        onClick={() => changeFilter({ didInvalidate: true, page: 0 })}
                    />
                </button>
            </AutoRow>
            <TableList
                {...rest}
                filter={filter}
                changeFilter={changeFilter}
                definitions={definitions}
                renderRow={renderRow}
                addListFeatures
                hideSearch
                hideExport
                hideOrder
            />
        </LoadBoxWrapper>
    )
}

export default LogsList
